import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslationService } from '@/layout/service/translation.service';
import { MetricChartComponent } from '@/shared/metric-chart/metric-chart.component';
import { MetricTileComponent } from '@/shared/ui/metric-tile/metric-tile.component';
import { LoadingSkeletonComponent } from '@/shared/ui/loading-skeleton/loading-skeleton.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { PduCardComponent } from '@/shared/power/pdu-card/pdu-card.component';
import { CloudPowerService } from '@/cloud/services/cloud-power.service';
import { CloudMetricsService, sourceSeries } from '../cloud-metrics.service';
import { InsightContext, InsightRange, InsightSummaryStore, insightRangeLabel } from '../insight-panel';
import { MetricQueryResult } from '../metrics.models';
import { POWER_SERVICE } from 'src/api/power.service.interface';
import { PowerDevice, PowerOutlet, applyPowerUpdate } from 'src/api/power';

/**
 * Room power: KPIs (now, energy, peak, average), usage per PDU over the selected range, and the
 * PDUs in the room with outlet control in a responsive grid. Registered as the "power" insight
 * panel; publishes "Power now" to the page's summary strip.
 */
@Component({
    selector: 'app-power-insight-panel',
    standalone: true,
    imports: [MetricChartComponent, MetricTileComponent, PduCardComponent, LoadingSkeletonComponent, EmptyStateComponent],
    providers: [CloudPowerService, { provide: POWER_SERVICE, useExisting: CloudPowerService }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="kpi-row">
            <app-metric-tile [label]="t()('power.now')" [value]="nowWatts()" unit="W" icon="fa fa-bolt" format="1.0-0" />
            <app-metric-tile [label]="t()('power.energyRange')" [value]="summary()?.['energyKWh']" unit="kWh" icon="fa fa-battery-half" format="1.0-2" [caption]="rangeLabel()" />
            <app-metric-tile [label]="t()('power.peak')" [value]="summary()?.['peakWatts']" unit="W" icon="fa fa-arrow-trend-up" format="1.0-0" [caption]="rangeLabel()" />
            <app-metric-tile [label]="t()('power.average')" [value]="summary()?.['avgWatts']" unit="W" icon="fa fa-wave-square" format="1.0-0" [caption]="rangeLabel()" />
        </div>

        @if (loadingHistory() && !result()) {
            <app-loading-skeleton class="history-loading" preset="tiles" [count]="1" [label]="t()('power.usageHistory')" />
        } @else if (historyError()) {
            <app-empty-state class="history-error" icon="fa fa-circle-exclamation" tone="error" [compact]="true" [title]="t()('power.historyFailed')" [actionLabel]="t()('insights.retry')" actionIcon="pi pi-refresh" (action)="retryHistory()" />
        } @else {
            <app-metric-chart [series]="series()" unit="W" [stacked]="true" [windowHours]="range().hours" [ariaLabel]="t()('power.usageHistory')" [emptyText]="t()('power.historyPending')" />
        }

        @if (devices().length) {
            <h3 class="section-title">{{ t()('power.devicesInRoom') }}</h3>
            <div class="pdu-list">
                @for (device of devices(); track device.key) {
                    <div class="pdu-item">
                        <app-pdu-card [device]="device" [energyKWh]="deviceEnergy()[device.key]" (outletChanged)="mergeOutlet($event)" />
                    </div>
                }
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                gap: var(--app-space-4);
                min-width: 0;
            }
            .kpi-row {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(min(100%, 10rem), 1fr));
                gap: var(--app-space-3);
            }
            .section-title {
                margin: 0;
                padding-top: var(--app-space-4);
                border-top: 1px solid var(--surface-border);
                font-size: 0.9375rem;
                font-weight: 600;
            }
            .pdu-list {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(min(100%, 30rem), 1fr));
                gap: var(--app-space-4);
            }
            .pdu-item {
                min-width: 0;
                padding: var(--app-space-4);
                border-radius: var(--content-border-radius, 0.5rem);
                background: var(--app-surface-inset, var(--surface-ground));
            }
        `
    ]
})
export class PowerInsightPanelComponent {
    private power = inject(CloudPowerService);
    private metrics = inject(CloudMetricsService);
    private destroyRef = inject(DestroyRef);
    private summaryStore = inject(InsightSummaryStore, { optional: true });
    readonly t = inject(TranslationService).t;

    readonly context = input.required<InsightContext>();
    readonly range = input.required<InsightRange>();

    readonly devices = signal<PowerDevice[]>([]);
    readonly result = signal<MetricQueryResult | null>(null);
    readonly loadingHistory = signal(true);
    readonly historyError = signal(false);

    /** Only the room identity - the context also changes on live usage updates, which must not refetch. */
    private readonly locationId = computed(() => this.context().id);
    private readonly namespaceId = computed(() => this.context().namespaceId);
    private historyRequest = 0;

    readonly summary = computed(() => this.result()?.summary ?? null);
    readonly deviceEnergy = computed(() => Object.fromEntries(this.devices().map((device) => [device.key, this.result()?.sources.find((source) => source.linkedDeviceId === device.key)?.summary['energyKWh'] ?? null])));
    readonly series = computed(() => sourceSeries(this.result(), 'avgWatts'));
    readonly nowWatts = computed(() => {
        const totals = this.devices()
            .map((d) => d.total?.watts)
            .filter((w): w is number => w != null);
        return totals.length ? totals.reduce((a, b) => a + b, 0) : null;
    });
    readonly rangeLabel = computed(() => insightRangeLabel(this.range().hours, this.t()));

    constructor() {
        this.power.updates$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((update) => this.devices.update((list) => list.map((d) => applyPowerUpdate(d, update))));

        // Reload equipment when the room changes.
        effect(() => {
            const locationId = this.locationId();
            const namespaceId = this.namespaceId();
            untracked(() => {
                this.power.setScope({ kind: 'location', namespaceId, locationId });
                void this.power
                    .getDevices()
                    .then((d) => this.devices.set(d))
                    .catch(() => this.devices.set([]));
            });
        });

        // Reload history when the room or range changes.
        effect(() => {
            const locationId = this.locationId();
            const range = this.range();
            untracked(() => void this.loadHistory(locationId, range));
        });

        // "Power now" in the page's summary strip.
        effect(() => {
            const watts = this.nowWatts();
            const label = this.t()('insights.powerNow');
            untracked(() => this.summaryStore?.set('power', watts == null ? [] : [{ key: 'power-now', label, value: watts, unit: 'W', icon: 'fa fa-bolt', format: '1.0-0' }]));
        });
        this.destroyRef.onDestroy(() => this.summaryStore?.clear('power'));
    }

    mergeOutlet(event: { key: string; outlet: PowerOutlet }): void {
        this.devices.update((list) => list.map((d) => applyPowerUpdate(d, { key: event.key, outlets: [event.outlet] })));
    }

    retryHistory(): void {
        void this.loadHistory(this.locationId(), this.range());
    }

    private async loadHistory(locationId: string, range: InsightRange): Promise<void> {
        const request = ++this.historyRequest;
        this.loadingHistory.set(true);
        this.historyError.set(false);
        try {
            const result = await this.metrics.getLocationMetrics(locationId, { type: 'power', from: range.from, to: range.to, includeBreakdown: false });
            if (request === this.historyRequest) this.result.set(result);
        } catch {
            if (request !== this.historyRequest) return;
            this.result.set(null);
            this.historyError.set(true);
        } finally {
            if (request === this.historyRequest) this.loadingHistory.set(false);
        }
    }
}

/** Power panel is shown when the room has at least one PDU. */
export function roomHasPowerDevices(context: InsightContext): boolean {
    return context.linkedDevices.some((d) => !!d.capabilities?.['hasOutlets']);
}
